"use client";
import { cn } from "@/lib/utils";
import { ResetIcon } from "@radix-ui/react-icons";
import { ChevronDown } from "lucide-react";
import React, {
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ComboBox,
  ComboBoxOptionType,
  DropdownInput,
  DropdownOptionType,
  TooltipProps,
} from ".";
import HexColorPicker from "./color-picker/HexColorPicker";
import { rgbaToHex } from "./color-picker/utils/convert";

interface ColorInputProps {
  value: string;
  evaluatedValue?: string;
  setValue: (val: string) => void;
  set: boolean;
  setSet: (set: boolean) => void;
  comboboxOptions?: ComboBoxOptionType[];
  onComboboxSelected?: (option: ComboBoxOptionType) => void;
  onValueUpdate?: (option: ComboBoxOptionType) => void; // Must be supplied with build arbitrary class fn
  dropdownOptions?: DropdownOptionType[];
  onDropdownSelected?: (option: DropdownOptionType) => void;
  buildArbitraryClass?: (input: string) => ComboBoxOptionType;
  buildOptionPreview?: (option: ComboBoxOptionType) => ReactNode;
  onInputFocus?: (event: React.FocusEvent<HTMLInputElement, Element>) => void;
  disabled?: boolean;
  onClear?: () => void;
  showHex?: boolean;
}

const CustomColorPicker: React.FC<ColorInputProps & TooltipProps> = ({
  value,
  evaluatedValue,
  setValue,
  comboboxOptions,
  onComboboxSelected,
  onValueUpdate,
  buildArbitraryClass,
  buildOptionPreview,
  set = false,
  setSet,
  onInputFocus,
  disabled = false,
  tooltipContent = null,
  onClear,
  dropdownOptions = [],
  onDropdownSelected,
  showHex = true,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [comboboxOpen, setComboboxOpen] = useState<null | boolean>(false);
  const [focused, setFocused] = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState<null | boolean>(false);
  const [hovered, setHovered] = useState(false);
  const colorInputRef = useRef<HTMLInputElement>(null);

  const onInputChange = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      setValue(ev.target.value || "");
      setComboboxOpen(true);
    },
    [setValue]
  );

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== "Enter") {
      return;
    }

    if (value === "" && onClear) {
      setComboboxOpen(false);
      onClear();

      if (inputRef.current) {
        inputRef.current.blur();
      }
    }
  };

  const handleDropdownOptionClick = (option: DropdownOptionType) => {
    if (option.value === "clear" && onClear) {
      onClear();
    } else if (onDropdownSelected) {
      onDropdownSelected(option);
    }

    setDropdownVisible(false);
  };

  const enhancedDropdownOptions = useMemo(() => {
    if (onClear && set) {
      const clearDropdownOption: DropdownOptionType = {
        label: "Clear style",
        value: "clear",
        icon: <ResetIcon />,
      };

      // Return a new array with the 'Clear' option appended
      return [...dropdownOptions, clearDropdownOption];
    }

    return dropdownOptions;
  }, [dropdownOptions, onClear, set]);

  const colorPickerValue = useMemo(() => {
    // check if value is a tailwind color class

    if (
      value.startsWith("bg-") ||
      value.startsWith("rgb(") ||
      (value.includes("-") && value.split("-").length == 2)
    ) {
      let newValue = value;

      if (value.startsWith("bg-")) {
        // @ts-expect-error TS18048
        const equivalentColor = comboboxOptions.find((option) => {
          return option.tailwind === value;
        });

        if (equivalentColor) {
          newValue = equivalentColor.css;
        }
      }

      if (value.includes("-") && value.split("-").length == 2) {
        // @ts-expect-error TS18048
        const equivalentColor = comboboxOptions.find((option) => {
          return option.tailwind === `bg-${value}`;
        });

        if (equivalentColor) {
          newValue = equivalentColor.css;
        }
      }

      // clean up the css value
      if (newValue.startsWith("background-color: ")) {
        newValue = newValue
          .replace("background-color: ", "")
          .replace(";", "")
          .trim();
      }

      if (newValue.startsWith("rgb(")) {
        const rgbaValue = newValue
          .replace("rgba(", "")
          .replace("rgb(", "")
          .replace(")", "")
          .replace(/,/g, "")
          .trim()
          .split(" ");

        return rgbaToHex({
          r: Number(rgbaValue[0]),
          g: Number(rgbaValue[1]),
          b: Number(rgbaValue[2]),
          a: Number(rgbaValue[3]) || 1,
        });
      }

      return newValue;
    }

    // check if value start with -- and end with -
    if (value.startsWith("--")) {
      const newValue = value.replace(/-/g, "");

      if (newValue.startsWith("rgb(") || newValue.startsWith("rgba(")) {
        const rgbaValue = newValue
          .replace("rgba(", "")
          .replace("rgb(", "")
          .replace(")", "")
          .replace(/,/g, "")
          .trim()
          .split(" ");

        return rgbaToHex({
          r: Number(rgbaValue[0]),
          g: Number(rgbaValue[1]),
          b: Number(rgbaValue[2]),
          a: Number(rgbaValue[3]) || 1,
        });
      }

      return newValue.startsWith("#") ? newValue : `#${newValue}`;
    }

    return value;
  }, [value]);

  const handleComboboxOptionClick = (option: ComboBoxOptionType) => {
    if (onComboboxSelected) {
      setSet(true);
      onComboboxSelected(option);
    }
    setComboboxOpen(false);

    const value = safeExtractValueFromCss(option.css);

    if (value) {
      setValue(value);
    }

    if (inputRef.current) {
      inputRef.current.blur();
    }
  };

  const safeExtractValueFromCss = (css: string): string | null => {
    try {
      const firstCss = css.split("\n")[0];
      let value: string = firstCss.split(":")[1];
      value = value.split(";")[0];

      return value.trim();
    } catch (error) {
      return null;
    }
  };

  return (
    <div className="w-full">
      <HexColorPicker
        id="hex-color-picker"
        color={colorPickerValue}
        onChange={(color) => {
          setValue(color);
          if (buildArbitraryClass) {
            // @ts-expect-error TS2722
            onValueUpdate(buildArbitraryClass(color));
          }
        }}
        onMouseUp={(event) => {
          event.preventDefault();
          // @ts-expect-error TS2722
          onComboboxSelected(buildArbitraryClass(colorPickerValue));
        }}
      />
      {showHex == true && (
        <div ref={colorInputRef}>
          {/*
           // @ts-expect-error TS2322 */}
          <div
            className="flex justify-between items-center mb-2"
            data-tooltip-id="styling-panel-tooltip"
            data-tooltip-content={tooltipContent}
            data-tooltip-place="bottom"
            data-tooltip-offset={5}
            data-tooltip-hidden={comboboxOpen || dropdownVisible}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onMouseDown={(event) => {
              if (event.button === 2) {
                // Right click
                event.preventDefault();
              }
            }}
            onContextMenu={(e) => {
              e.preventDefault();
              if (
                enhancedDropdownOptions &&
                enhancedDropdownOptions.length > 0
              ) {
                setDropdownVisible(true);
              }
            }}
          >
            <div className="text-xs text-secondary-800 font-medium">Hex</div>
            <div
              className={cn(
                "flex items-center gap-1 w-full max-w-[140px] h-7 bg-secondary-n-950 rounded-md relative",
                focused && "border border-primary-400"
              )}
            >
              <button
                className="ml-1 w-3 h-3 rounded-xs shadow-inner cursor-pointer absolute"
                style={{ backgroundColor: value }}
              ></button>
              <input
                ref={inputRef}
                disabled={disabled}
                value={value}
                onChange={onInputChange}
                onFocus={(event) => {
                  event.target.select();
                  setComboboxOpen(true);
                  setFocused(true);

                  if (onInputFocus) {
                    onInputFocus(event);
                  }
                }}
                onKeyDown={handleKeyDown}
                onBlur={() => {
                  setFocused(false);
                }}
                className={`text-2xs px-5 border-none outline-none font-medium w-full h-full rounded-md bg-secondary-n-950 ${
                  disabled || !set ? "text-secondary-600" : "text-white"
                }`}
              />

              <button
                onClick={() => setDropdownVisible(true)}
                className="absolute right-1"
              >
                <ChevronDown className="text-secondary-n-500 h-4 w-4" />
              </button>

              <DropdownInput
                className="w-40"
                dropdownOptions={enhancedDropdownOptions}
                onDropdownSelected={handleDropdownOptionClick}
                // @ts-expect-error TS2322
                visible={dropdownVisible}
                setVisible={setDropdownVisible}
              />
            </div>
            {comboboxOpen && comboboxOptions && (
              <ComboBox
                input={(value || "").toString()}
                options={comboboxOptions}
                open={comboboxOpen}
                setOpen={setComboboxOpen}
                onOptionSelected={(option: ComboBoxOptionType) =>
                  handleComboboxOptionClick(option)
                }
                buildOptionPreview={buildOptionPreview}
                buildArbitraryClass={buildArbitraryClass}
                anchorRef={colorInputRef}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(CustomColorPicker);
